import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from '../notification/notification.service';
import { OrgManagementApi } from '../org-management-api/org-management-api';
import { DocumentUpdateRequest, AssessmentUpdateRequest } from '../../models';

interface NameUpdateResult {
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentNameService {
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly notifications = inject(NotificationService);
  private readonly transloco = inject(TranslocoService);

  /** A refused rename is shown, with the server's reason when it gave one (an empty name, a document
   *  archived or changed meanwhile), so it never fails silently. */
  private failed(error: unknown, what: 'document name' | 'assessment name'): NameUpdateResult {
    console.error(`❌ DocumentNameService: Failed to update ${what}`, error);
    this.notifications.errorFromException(
      error,
      this.transloco.translate('panels_ext.rename_failed')
    );
    return { success: false, error: `Failed to update ${what}` };
  }

  /**
   * Update document name
   */
  updateDocumentName(documentId: string, newName: string): Observable<NameUpdateResult> {
    if (!documentId || !newName?.trim()) {
      return of({ success: false, error: 'Document ID and name are required' });
    }

    const updateData: DocumentUpdateRequest = {
      name: newName.trim(),
    };

    return this.orgManagementApi.updateDocument(documentId, updateData).pipe(
      map(() => {
        console.debug('📄 DocumentNameService: Document name updated');
        return { success: true };
      }),
      catchError(error => of(this.failed(error, 'document name')))
    );
  }

  /**
   * Update assessment name
   */
  updateAssessmentName(
    orgId: string,
    assessmentId: string,
    newName: string
  ): Observable<NameUpdateResult> {
    if (!orgId || !assessmentId || !newName?.trim()) {
      return of({
        success: false,
        error: 'Organization ID, assessment ID, and name are required',
      });
    }

    const updateData: AssessmentUpdateRequest = {
      assessment_name: newName.trim(),
    };

    return this.orgManagementApi.updateAssessment(orgId, assessmentId, updateData).pipe(
      map(() => {
        console.debug('📝 DocumentNameService: Assessment name updated');
        return { success: true };
      }),
      catchError(error => of(this.failed(error, 'assessment name')))
    );
  }
}
